/**
 * Client-side mirror of falcon-mailer's replaceVariables, for previews.
 *
 * Drafts and step templates store merge tags unresolved by design; the
 * mailer substitutes them at send time. Any UI that presents itself as "the
 * email" must apply the same substitution, or the approval flow asks the
 * user to sign off on literal braces — which is exactly what the Sends tab
 * did.
 *
 * Semantics mirrored from the mailer: a known tag becomes its value, else
 * its inline |fallback, else "there" for firstName, else empty. Unknown
 * tags stay visible, matching the mailer's refusal to silently blank a
 * typo. Keep this list in step with the mailer's replaceVariables.
 */

export interface MergeTagValues {
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  email?: string | null;
  linkedinUrl?: string | null;
  senderFirstName?: string | null;
  senderLastName?: string | null;
}

const TAG_RE = /\{\{\s*(\w+)\s*(?:\|([^}]*))?\}\}/g;

const KNOWN = new Set([
  'firstName',
  'lastName',
  'company',
  'email',
  'linkedinUrl',
  'senderFirstName',
  'senderLastName',
]);

export function renderMergeTags(text: string, values: MergeTagValues): string {
  return text.replace(TAG_RE, (match, name: string, fallback?: string) => {
    if (!KNOWN.has(name)) return match;
    const value = (values[name as keyof MergeTagValues] || '').trim();
    if (value) return value;
    const fb = (fallback || '').trim();
    if (fb) return fb;
    return name === 'firstName' ? 'there' : '';
  });
}

/** "Laura Lewis" -> { first: "Laura", last: "Lewis" }. */
export function splitName(full?: string | null): { first: string; last: string } {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}
